import { VEHICLE_SEARCH_STORAGE_KEY, OPENSEARCH_AUTH, OPENSEARCH_URL } from './constants';
import * as mockService from './mockApi';
import * as serviceUtils from './utils';

function getStorefrontToken() {
  const token = window.Grafite.ResearchVehicle?.graphqlStoreFrontToken;
  return typeof token === 'string' && token.trim() !== '' ? token.trim() : null;
}

async function storefrontGraphql(query, variables = {}, signal = null) {
  const token = getStorefrontToken();
  if (!token) {
    console.error('Vehicle search: missing Storefront API token in theme settings.');
    return null;
  }

  const response = await fetch(window.Grafite.ResearchVehicle?.graphqlStoreFrontUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Shopify-Storefront-Access-Token': token,
    },
    body: JSON.stringify({ query, variables }),
    signal,
  });

  if (!response.ok) {
    throw new Error(`GraphQL request failed: ${response.status}`);
  }

  const payload = await response.json();

  if (payload.errors?.length) {
    throw new Error(payload.errors.map((e) => e.message).join('; '));
  }

  return payload.data;
}

async function getOpenSearchProductsSkusFromVehicleSearchStorage() {
  const vehicleSearchStorage = localStorage.getItem(VEHICLE_SEARCH_STORAGE_KEY);
  if (!vehicleSearchStorage) {
    return [];
  }

  const { brand, model, year } = JSON.parse(vehicleSearchStorage);

  const url = window.Grafite.ResearchVehicle?.openSearchUrl;
  const auth = window.Grafite.ResearchVehicle?.openSearchAuth;
  if (!url || !auth) {
    console.error('Vehicle search: missing Open Search URL or auth in theme settings.');
    return [];
  }

  const options = {
    method: 'POST',
    headers: {
      authorization: auth,
      'content-type': 'application/json',
    },
    body: JSON.stringify({
      size: 10000,
      _source: ['sku', 'descrizione'],
      query: {
        nested: {
          path: 'compatibilita',
          query: {
            bool: {
              must: [
                { term: { 'compatibilita.brand': brand } },
                { term: { 'compatibilita.modello': model } },
                { term: { 'compatibilita.anni': year } },
              ],
            },
          },
        },
      },
    }),
  };

  try {
    const response = await fetch(url, options);
    const data = await response.json();
    return Array.isArray(data.hits.hits) ? new Set(data.hits.hits.map((hit) => hit._source.sku)) : new Set();
  } catch (error) {
    console.error(error);
    return new Set();
  }
}

async function getMockedOpenSearchProductsSkus() {
  try {
    const response = await fetch(window.Grafite.ResearchVehicle?.mockedVehiclesSkusUrl ?? '');
    const data = await response.json();
    return Array.isArray(data?.skus) ? new Set(data.skus) : new Set();
  } catch (error) {
    console.error('Failed to load mocked SKUs:', error);
    return new Set();
  }
}

/** @typedef {{ id: string, label: string }} VehicleOption */
/**
 * @typedef {{
 *   type: 'moto' | 'bici',
 *   brand: string,
 *   model: string,
 *   year: string
 * }} Vehicle
 */

function getDataSource() {
  return window.Grafite?.ResearchVehicle?.dataSource === 'api' ? 'api' : 'mock';
}

async function searchOpenSearch(body) {
  const response = await fetch(OPENSEARCH_URL, {
    method: 'POST',
    headers: {
      authorization: OPENSEARCH_AUTH,
      'content-type': 'application/json',
    },
    body: JSON.stringify(body),
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(`OpenSearch error: ${response.status}`);
  }

  return data;
}

async function getApiBrands() {
  const data = await searchOpenSearch({
    size: 0,
    aggs: {
      nested_compat: {
        nested: {
          path: 'compatibilita',
        },
        aggs: {
          distinct_brands: {
            terms: {
              field: 'compatibilita.brand',
              size: 1000,
            },
          },
        },
      },
    },
  });

  return serviceUtils.toOptions(data.aggregations?.nested_compat?.distinct_brands?.buckets);
}

async function getApiModels(brand) {
  const data = await searchOpenSearch({
    size: 0,
    aggs: {
      nested_compat: {
        nested: {
          path: 'compatibilita',
        },
        aggs: {
          filtro_brand: {
            filter: {
              term: {
                'compatibilita.brand': brand,
              },
            },
            aggs: {
              distinct_modelli: {
                terms: {
                  field: 'compatibilita.modello',
                  size: 1000,
                },
              },
            },
          },
        },
      },
    },
  });

  return serviceUtils.toOptions(data.aggregations?.nested_compat?.filtro_brand?.distinct_modelli?.buckets);
}

async function getApiYears(brand, model) {
  const data = await searchOpenSearch({
    size: 0,
    aggs: {
      nested_compat: {
        nested: {
          path: 'compatibilita',
        },
        aggs: {
          filtro_veicolo: {
            filter: {
              bool: {
                must: [
                  {
                    term: {
                      'compatibilita.brand': brand,
                    },
                  },
                  {
                    term: {
                      'compatibilita.modello': model,
                    },
                  },
                ],
              },
            },
            aggs: {
              distinct_anni: {
                terms: {
                  field: 'compatibilita.anni',
                  size: 1000,
                },
              },
            },
          },
        },
      },
    },
  });

  return serviceUtils.toOptions(data.aggregations?.nested_compat?.filtro_veicolo?.distinct_anni?.buckets);
}

async function getApiCompatibleItems(brand, model, year) {
  const data = await searchOpenSearch({
    _source: ['sku', 'descrizione'],
    query: {
      nested: {
        path: 'compatibilita',
        query: {
          bool: {
            must: [
              {
                term: {
                  'compatibilita.brand': brand,
                },
              },
              {
                term: {
                  'compatibilita.modello': model,
                },
              },
              {
                term: {
                  'compatibilita.anni': Number(year),
                },
              },
            ],
          },
        },
      },
    },
  });

  return data.hits?.hits?.map((hit) => hit._source) ?? [];
}

async function getApiCompatibleVehiclesForProduct(sku) {
  if (!sku) return [];

  const data = await searchOpenSearch({
    _source: ['compatibilita'],
    query: {
      term: {
        sku,
      },
    },
  });

  const compatibilita = data.hits?.hits?.flatMap((hit) => hit._source?.compatibilita ?? []) ?? [];

  return compatibilita.map(serviceUtils.normalizeVehicleCompatibility);
}

/**
 * @param {'moto' | 'bici'} vehicleType
 * @returns {Promise<VehicleOption[]>}
 */
async function getBrands(vehicleType) {
  return getDataSource() === 'api' ? getApiBrands(vehicleType) : mockService.getMockBrands(vehicleType);
}

/**
 * @param {string} brand
 * @returns {Promise<VehicleOption[]>}
 */
async function getModels(brand) {
  return getDataSource() === 'api' ? getApiModels(brand) : mockService.getMockModels(brand);
}

/**
 * @param {string} brand
 * @param {string} model
 * @returns {Promise<VehicleOption[]>}
 */
async function getYears(brand, model) {
  return getDataSource() === 'api' ? getApiYears(brand, model) : mockService.getMockYears(brand, model);
}

/**
 * @param {string} brand
 * @param {string} model
 * @param {string | number} year
 */
async function getCompatibleItems(brand, model, year) {
  return getDataSource() === 'api'
    ? getApiCompatibleItems(brand, model, year)
    : mockService.getMockCompatibleItems(brand, model, year);
}

/**
 * @param {string} sku
 * @returns {Promise<Vehicle[]>}
 */
async function getCompatibleVehiclesForProduct(sku) {
  return getDataSource() === 'api'
    ? getApiCompatibleVehiclesForProduct(sku)
    : mockService.getMockCompatibleVehiclesForProduct(sku);
}

function isVehicleCompatible(vehicle, compatibleVehicles) {
  if (!vehicle || !Array.isArray(compatibleVehicles)) return false;
  if(window.Grafite?.ResearchVehicle?.genericProduct === 'Generic' || window.Grafite?.ResearchVehicle?.genericProduct === 'GenericMotorbike') return true;

  return compatibleVehicles.some((compatibleVehicle) => {
    const compatibleYears = String(compatibleVehicle.year)
      .split(',')
      .map((year) => year.trim());

    return (
      compatibleVehicle.type === vehicle.type &&
      compatibleVehicle.brand === vehicle.brand &&
      compatibleVehicle.model === vehicle.model &&
      compatibleYears.includes(String(vehicle.year).trim())
    );
  });
}

function readSavedVehicle() {
  try {
    const raw = localStorage.getItem(VEHICLE_SEARCH_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function saveVehicle(vehicle) {
  try {
    localStorage.setItem(VEHICLE_SEARCH_STORAGE_KEY, JSON.stringify(vehicle));
  } catch {}
}

function clearSavedVehicle() {
  try {
    localStorage.removeItem(VEHICLE_SEARCH_STORAGE_KEY);
  } catch {}
}

function formatVehicle(vehicle) {
  if (!vehicle) return '';
  if(window.Grafite?.ResearchVehicle?.translations?.[vehicle.brand]) return window.Grafite?.ResearchVehicle?.translations?.[vehicle.brand] ?? '';

  const brand = vehicle.brand || '';
  const model = vehicle.model || '';

  const cc = vehicle.model ? `cc` : '';

  const yearFrom = vehicle.yearFrom ?? '';
  const yearTo = vehicle.yearTo ?? '';

  const years = yearFrom && yearTo && (yearFrom !== yearTo) ? `${yearFrom}/${yearTo}` : yearFrom || yearTo || vehicle.year || '';

  const note = vehicle.note || '';

  return [brand, model, cc, years, note].filter(Boolean).join(' ').toUpperCase();
}

async function getProductRecommendations(productId, limit, intent) {
  const response = await fetch(window.Shopify.routes.root + `recommendations/products.json?product_id=${productId}&limit=${limit}&intent=${intent}`);
  const data = await response.json();
  return data;
}

export {
  getMockedOpenSearchProductsSkus,
  getOpenSearchProductsSkusFromVehicleSearchStorage,
  storefrontGraphql,
  getStorefrontToken,
  clearSavedVehicle,
  saveVehicle,
  readSavedVehicle,
  isVehicleCompatible,
  getCompatibleVehiclesForProduct,
  getCompatibleItems,
  getYears,
  getBrands,
  getModels,
  formatVehicle,
  getProductRecommendations,
};
